import appSettings from "../settings/appSettings";
import fetchRequest from "./fetchRequest";
import { logToClientLogger } from "../helpers/clientLogger.js";
const { feedsApiConfig, subscriptionKey } = appSettings;
const { baseUrl } = feedsApiConfig;

// Cache timeout in milliseconds (15 minutes)
const CACHE_TIMEOUT = 15 * 60 * 1000;
// SessionStorage key prefix
const CACHE_KEY_PREFIX = "project_data_";

export function getMapImage(params) {
  const { lat, lon, scale="2", width="320", height="220", mapType="roadmap", zoom="5" } = params;
  const key = process.env.REACT_APP_GOOGLE_MAP_API_KEY;
  const size = `${width}x${height}`;
  const location = `${lat},${lon}`;
  const imageUrl = `https://maps.googleapis.com/maps/api/staticmap?key=${key}&markers=size:mid%7Ccolor:red%7C${location}&size=${size}&scale=${scale}&center=${location}&zoom=${zoom}&format=png&maptype=${mapType}&style=feature:administrative%7Celement:geometry%7Cvisibility:off&style=feature:administrative.land_parcel%7Cvisibility:off&style=feature:administrative.neighborhood%7Cvisibility:off&style=feature:poi%7Cvisibility:off&style=feature:poi%7Celement:labels.text%7Cvisibility:off&style=feature:road%7Celement:labels%7Cvisibility:off&style=feature:road%7Celement:labels.icon%7Cvisibility:off&style=feature:road.arterial%7Cvisibility:off&style=feature:road.highway%7Celement:labels%7Cvisibility:off&style=feature:road.local%7Cvisibility:off&style=feature:transit%7Cvisibility:off&style=feature:water%7Celement:labels.text%7Cvisibility:off`;
  return imageUrl;
}

export function parseDataFields(fieldNames, data) {
  return fieldNames.reduce((fields, fieldName) => {
    const fieldValue = data[fieldName];
    if (typeof fieldValue === "undefined") {
      throw new Error(`Not able to parse the ${fieldName}.`);
    }

    fields[fieldName] = fieldValue;

    return fields;
  }, {});
}

/**
 * Gets cached data from sessionStorage if available and not expired
 * @param {string} projectId - The project ID to get cached data for
 * @returns {object|null} The cached data or null if not found/expired
 */
export function getCachedData(projectId) {
  try {
    const cacheKey = `${CACHE_KEY_PREFIX}${projectId}`;
    const cachedItem = sessionStorage.getItem(cacheKey);

    if (!cachedItem) return null;

    const { data, timestamp } = JSON.parse(cachedItem);
    const now = Date.now();

    // Check if cache has expired
    if (now - timestamp > CACHE_TIMEOUT) {
      // Remove expired cache
      sessionStorage.removeItem(cacheKey);
      return null;
    }

    return data;
  } catch (err) {
    console.warn("Error accessing session storage cache:", err);
    return null;
  }
}

/**
 * Saves data to sessionStorage cache
 * @param {string} projectId - The project ID to cache data for
 * @param {object} data - The data to cache
 */
function setCachedData(projectId, data) {
  try {
    const cacheKey = `${CACHE_KEY_PREFIX}${projectId}`;
    const cacheItem = {
      data,
      timestamp: Date.now()
    };

    sessionStorage.setItem(cacheKey, JSON.stringify(cacheItem));
  } catch (err) {
    console.warn("Error saving to session storage cache:", err);
  }
}

export async function requestCommunityNewsData(projectId) {
  // Check if we have a valid cached response in sessionStorage, or hit service
  const cachedData = getCachedData(projectId);
  if (cachedData) {
    return cachedData;
  }

  const cnAdpUrl = `${baseUrl}/references/projects/${projectId}`;

  try {
    const resp = await fetchRequest(cnAdpUrl, {
      "method": "GET",
      "headers": {
        "Ocp-Apim-Subscription-Key": subscriptionKey,
        "Content-Type": "application/json"
      },
      "timeout": 30 * 1000
    });

    if (!resp.ok) {
      throw new Error(`Error retreiving cn service data. url: ${cnAdpUrl}`);
    }

    const data = await resp.json();

    setCachedData(projectId, data);

    return data;
  } catch (err) {
    console.log(err);
    return Promise.reject(err);
  }
}

/**
 * Request country data by country code
 * @param {string} countryCode - The three-letter country code (e.g., "UGA")
 * @returns {Promise<object>} - The country data
 */
export async function requestCountryData(countryCode) {
  // Use a unique cache key prefix for country data
  const COUNTRY_CACHE_KEY_PREFIX = "country_data_";

  // Check for cached country data
  const cacheKey = `${COUNTRY_CACHE_KEY_PREFIX}${countryCode}`;
  try {
    const cachedItem = sessionStorage.getItem(cacheKey);
    if (cachedItem) {
      const { data, timestamp } = JSON.parse(cachedItem);
      const now = Date.now();

      // Check if cache has expired
      if (now - timestamp <= CACHE_TIMEOUT) {
        return data;
      }
      // Remove expired cache
      sessionStorage.removeItem(cacheKey);
    }
  } catch (err) {
    console.warn("Error accessing country data cache:", err);
  }

  const countryUrl = `${baseUrl}/references/countries/${countryCode}`;

  try {
    const resp = await fetchRequest(countryUrl, {
      "method": "GET",
      "headers": {
        "Ocp-Apim-Subscription-Key": subscriptionKey,
        "Content-Type": "application/json"
      },
      "timeout": 30 * 1000
    });

    if (!resp.ok) {
      logToClientLogger(`Error retrieving country data. url: ${countryUrl}`, "error");
      throw new Error(`Error retrieving country data. url: ${countryUrl}`);
    }

    const data = await resp.json();

    // Validate the response data
    if (!data || typeof data !== 'object') {
      const error = new Error('Invalid country data format received');
      logToClientLogger(`Invalid country data format received for ${countryCode}`, "error");
    }

    // For example, check for required fields like 'name', 'code', etc.
    const requiredFields = ['name', 'nationalOfficeFullAddress'];
    const missingFields = requiredFields.filter(field => !data.hasOwnProperty(field));

    if (missingFields.length > 0) {
      logToClientLogger(`Country data missing required fields: ${missingFields.join(', ')} for ${countryCode}`, "warn");
      // We can still proceed with incomplete data, but we've logged the warning
    }

    // Cache the country data
    try {
      const cacheItem = {
        data,
        timestamp: Date.now()
      };

      sessionStorage.setItem(cacheKey, JSON.stringify(cacheItem));
    } catch (err) {
      console.warn("Error saving country data to cache:", err);
    }

    return data;
  } catch (err) {
    console.log(err);
    return Promise.reject(err);
  }
}
